import { Link, useParams } from "react-router-dom";
import {
  aberturaDoCard,
  cardsDoCurso,
  formatarData,
  useHub,
  type CardVitrine,
  type CursoDoAluno,
} from "../lib/dados";
import Carregando from "../componentes/Carregando";
import Rodape from "../componentes/Rodape";
import AvisoContrato from "../componentes/AvisoContrato";
import {
  Aviso,
  CARD_EM_BREVE,
  ChipDoCard,
  LinkDoCard,
  SeloVigencia,
  SetaDireita,
  SinalDeAbertura,
  Tile,
  Topo,
} from "../componentes/ui";
import { IconeDoCard } from "../componentes/icones";
import { CURSOS_COM_IMAGEM, imagemDoCard } from "../lib/imagensDeCard";
import { useNovidades, type Novidade } from "../lib/novidades";
import { faltamResponder, useMinhasPesquisas, type PesquisaDoAluno } from "../lib/pesquisas";

// A tela do curso — o único lugar onde as ferramentas aparecem.
//
// Duas seções, sempre na mesma ordem: as portas de sempre (Aulas, Comunidade,
// Área do Candidato, Área do Contratante — os quatro universais, que a
// vw_minha_vitrine marca com `universal`) e as ferramentas que este produto
// libera. Aulas ganha destaque de largura dupla de propósito: é o destino da
// maioria das visitas, e tem de ser impossível de errar.
export default function Curso() {
  const { slug } = useParams<{ slug: string }>();
  const estado = useHub();
  // A NOVIDADE de cada ferramenta (000304). O hook vem antes de qualquer
  // `return`: fora de ordem, ele derruba a rota com o React #310.
  const novidades = useNovidades();
  // As pesquisas abertas (000310), pelo mesmo motivo: o hook antes do `return`.
  const pesquisas = useMinhasPesquisas();

  if (estado.fase === "carregando") return <Carregando />;
  if (estado.fase === "erro") {
    return (
      <Aviso titulo="Algo deu errado">
        Não conseguimos carregar este curso agora. Recarregue a página; se continuar, fale com o suporte.
      </Aviso>
    );
  }
  if (estado.fase === "sem-vinculo") {
    return (
      <Aviso titulo="Não encontramos o seu cadastro" acao={<Link to="/" className="btn-contorno">Voltar ao início</Link>}>
        Nenhuma compra está ligada a este e-mail.
      </Aviso>
    );
  }

  const { hub } = estado;
  const curso = [...hub.cursos, ...hub.faculdade].find((c) => c.slug === slug);

  // Slug digitado à mão, curso vencido ou link antigo. Não é erro: é acesso que
  // o aluno não tem, e a resposta é a mesma que a RLS já dá — nada deste curso.
  if (!curso) {
    return (
      <div className="min-h-screen">
        <Topo atual="inicio" nome={hub.nome} avatarUrl={hub.avatarUrl} />
        <Aviso titulo="Curso não disponível" acao={<Link to="/" className="pill-cta">Ver os meus cursos</Link>}>
          Este curso não está na sua lista de acessos ativos. Se você comprou e ele não aparece, fale com
          o suporte que a gente confere.
        </Aviso>
      </div>
    );
  }

  // `cardsDeCurso` já exclui os de escopo 'conta' (Contrate um Contador), que
  // moram na tela inicial desde a 000038 — dentro dos cursos eles apareciam uma
  // vez por curso, como se pertencessem a cada um.
  const cards = cardsDoCurso(hub.cardsDeCurso, curso.slug);
  // O herói "Aulas" é o card universal `aulas` (o Memberkit). O curso cujas aulas MORAM no hub (000375,
  // o Fundamentos da Reforma) não recebe os universais e tem uma porta própria, interna: ela é o herói
  // dele, e sai da lista de ferramentas — senão as aulas do curso apareceriam como uma ferramenta.
  const aulas =
    cards.find((c) => c.card_slug === "aulas") ??
    cards.find((c) => c.card_tipo === "porta" && c.destino === "interno");
  const outrasPortas = cards.filter((c) => c.universal && c.card_slug !== "aulas");
  const ferramentas = cards.filter((c) => !c.universal && c !== aulas);
  // Seções do curso, quando existem. Curso sem seções (nove dos dez hoje) segue
  // com o layout de antes — com quatro ou cinco cards, seccionar é burocracia.
  const secoes = hub.secoesPorCurso.get(curso.slug);
  // N11/D27: nas áreas já 100% com imagem (Escola), o card vira a imagem do
  // Bubble. Fora delas, `imgDe` devolve null e segue o card estruturado.
  const imgDe = (slug: string) => imagemDoCard(slug, curso.slug);
  // Na área com imagem, a página inteira assume a "cara do Bubble": fundo claro
  // com a marca d'água do CF, e os cabeçalhos de seção no estilo do Bubble (chip
  // + nome + contador). Cores explícitas do Bubble (navy #0b1e42, dourado
  // #e89a38) para casar com os cards-imagem, que já são navy/dourado.
  const comImagem = CURSOS_COM_IMAGEM.has(curso.slug);

  return (
    <div
      className="min-h-screen"
      // A marca d'água do CF sobre branco, como no Bubble (center/cover/fixed).
      style={
        comImagem
          ? { background: "#ffffff url('/fundo-cf.png') center center / cover no-repeat fixed" }
          : undefined
      }
    >
      <Topo atual={curso.faculdade ? "faculdade" : "inicio"} nome={hub.nome} avatarUrl={hub.avatarUrl} />

      {/* Com o fundo branco fixo, o conteúdo é território CLARO também nos tokens
          (`tema-claro`, no index.css): com os do escuro, o "Voltar", os títulos
          das seções e o rodapé saíam #fff e #a6b0c3 sobre o branco. O Topo fica
          fora, no tema de quem usa, como em todas as outras telas. */}
      <div className={`mx-auto max-w-6xl px-4 pb-16 sm:px-6${comImagem ? " tema-claro" : ""}`}>
        <Link
          to={curso.faculdade ? "/faculdade" : "/"}
          className="mt-6 mb-4 inline-flex items-center gap-2 text-sm font-semibold text-cinza transition hover:text-titulo"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {curso.faculdade ? "Voltar para a Faculdade" : "Voltar ao início"}
        </Link>

        <div className="surface rise flex flex-wrap items-center justify-between gap-4 p-6 sm:p-7">
          <h1 className="max-w-2xl text-2xl font-bold text-titulo sm:text-3xl">{curso.nome}</h1>
          <SeloVigencia vigencia={curso} prefixo="Seu acesso" />
        </div>

        {curso.trancado && (
          <AvisoContrato curso={curso} urlContrato={hub.contratoUrlPorCurso.get(curso.slug)} />
        )}

        <ConviteDaPesquisa pesquisas={pesquisas.filter((p) => p.curso_slug === curso.slug)} />

        {secoes ? (
          // Curso com seções (000041): a tela segue o agrupamento por assunto que
          // Victor ditou, e não a divisão genérica portas/ferramentas. Aulas
          // continua sendo o herói de largura total — está na primeira seção.
          secoes.map((secao, iSecao) => {
            const daSecao = secao.cards
              .map((slug) => cards.find((c) => c.card_slug === slug))
              .filter((c): c is CardVitrine => Boolean(c));
            if (daSecao.length === 0) return null;
            return (
              <section key={secao.titulo} className="pt-10">
                {comImagem ? (
                  // Cabeçalho de seção como no Bubble: chip (quadrado navy + quadrado
                  // dourado dentro), o nome em caixa alta, a linha e o contador "N itens →".
                  <div className="mb-3 flex items-center gap-3.5">
                    <span
                      aria-hidden="true"
                      className="relative size-[30px] shrink-0 rounded-[9px] bg-[linear-gradient(150deg,#12315f,#0b1e42)] after:absolute after:inset-2 after:rounded-[4px] after:bg-[#e89a38] after:opacity-90 after:content-['']"
                    />
                    <h2 className="m-0 whitespace-nowrap text-[15px] font-extrabold uppercase tracking-[0.16em] text-[#0b1e42]">
                      {secao.titulo}
                    </h2>
                    <span className="h-px min-w-4 flex-1 bg-[#dde5f2]" />
                    <span className="inline-flex shrink-0 items-center gap-2 text-[13px] font-semibold tabular-nums text-[#6b7a99]">
                      <span className="font-extrabold text-[#e89a38]">{daSecao.length}</span>
                      {daSecao.length === 1 ? "item" : "itens"}
                      <SetaDireita className="size-4 text-[#e89a38]" />
                    </span>
                  </div>
                ) : (
                  <h2 className="text-xl font-bold text-titulo sm:text-2xl">{secao.titulo}</h2>
                )}
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {daSecao.map((card, i) => {
                    const img = imgDe(card.card_slug);
                    if (img) {
                      return (
                        <CardImagem
                          key={card.card_slug}
                          card={card}
                          curso={curso.slug}
                          imagem={img}
                          i={iSecao === 0 ? i + 1 : i}
                          travado={curso.trancado}
                          novidade={novidades.get(card.card_slug) ?? null}
                        />
                      );
                    }
                    return card.card_slug === "aulas" ? (
                      <CardAulas
                        key={card.card_slug}
                        card={card}
                        curso={curso.slug}
                        nomeCurso={curso.nome}
                        travado={curso.trancado}
                      />
                    ) : (
                      <CardFerramenta
                        key={card.card_slug}
                        card={card}
                        curso={curso.slug}
                        i={iSecao === 0 ? i + 1 : i}
                        travado={curso.trancado}
                        novidade={novidades.get(card.card_slug) ?? null}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })
        ) : (
          <>
            <section className="pt-10">
              <h2 className="text-2xl font-bold text-titulo">Suas portas</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {aulas &&
                  (imgDe("aulas") ? (
                    <CardImagem
                      card={aulas}
                      curso={curso.slug}
                      imagem={imgDe("aulas")!}
                      i={1}
                      travado={curso.trancado}
                    />
                  ) : (
                    <CardAulas card={aulas} curso={curso.slug} nomeCurso={curso.nome} travado={curso.trancado} />
                  ))}
                {outrasPortas.map((card, i) => {
                  const img = imgDe(card.card_slug);
                  return img ? (
                    <CardImagem
                      key={card.card_slug}
                      card={card}
                      curso={curso.slug}
                      imagem={img}
                      i={i + 2}
                      travado={curso.trancado}
                    />
                  ) : (
                    <CardPorta key={card.card_slug} card={card} curso={curso.slug} i={i + 2} travado={curso.trancado} />
                  );
                })}
              </div>
            </section>

            {ferramentas.length > 0 && (
              <section className="pt-10">
                <h2 className="text-2xl font-bold text-titulo">{rotuloDaSecao(curso)}</h2>
                <p className="mt-1 text-sm text-cinza">Incluídas no seu curso, sem custo extra.</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {ferramentas.map((card, i) => {
                    const img = imgDe(card.card_slug);
                    return img ? (
                      <CardImagem
                        key={card.card_slug}
                        card={card}
                        curso={curso.slug}
                        imagem={img}
                        i={i + 1}
                        travado={curso.trancado}
                        novidade={novidades.get(card.card_slug) ?? null}
                      />
                    ) : (
                      <CardFerramenta
                        key={card.card_slug}
                        card={card}
                        curso={curso.slug}
                        i={i + 1}
                        travado={curso.trancado}
                        novidade={novidades.get(card.card_slug) ?? null}
                      />
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}

        {/* O contrato do curso livre não tem outra casa: a Secretaria só existe
            na Faculdade. Aqui é informação de estado, não CTA — os contrato_url
            do catálogo ainda são provisórios (dívida aberta com o Léo), e um
            botão apontaria para link que não resolve. */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-3 border-t border-borda pt-6 text-sm text-cinza">
          <p>Precisa de ajuda com este curso? O canal está no rodapé desta página.</p>
          {curso.contrato && (
            <p>
              Contrato:{" "}
              <strong className="font-semibold text-tinta">
                {curso.contrato.status === "assinado"
                  ? `assinado em ${formatarData(curso.contrato.assinado_em)}`
                  : "aguardando assinatura"}
              </strong>
            </p>
          )}
        </div>

        <Rodape />
      </div>
    </div>
  );
}

function rotuloDaSecao(curso: CursoDoAluno): string {
  // Nome de pós é longo ("Pós-graduação em Departamento Pessoal e Legislação
  // Trabalhista") e repetir no título da seção estoura a linha. Nos cursos
  // livres o nome cabe e ajuda a orientar.
  return curso.faculdade ? "Ferramentas do curso" : `Ferramentas do ${curso.nome}`;
}

/**
 * Aulas: card herói, largura dupla, navy.
 *
 * O destaque não é enfeite — é o destino que a maioria dos alunos vem buscar.
 *
 * Desde a 000062 este card TEM destino: link mágico do Memberkit da escola do
 * curso, gerado no clique pela ponte `/abrir`. O que ainda faz o herói mostrar
 * "em breve" é curso trancado por contrato — e aí o chip diz "Assine para
 * liberar", que é outra coisa.
 */
function CardAulas({
  card,
  curso,
  nomeCurso,
  travado,
}: {
  card: CardVitrine;
  curso: string;
  nomeCurso: string;
  travado?: boolean;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <div>
        <h3 className="text-2xl font-bold text-white">Aulas</h3>
        <p className="mt-1 max-w-md text-navy-mute">
          {card.descricao ?? `Assista às aulas de ${nomeCurso} no portal do curso.`}
        </p>
      </div>
      {abre ? (
        <span className="pill-cta shrink-0">
          Ir para as aulas <SetaDireita />
          {/* Só o destino de fora abre em outra aba; as aulas que moram no hub abrem aqui. */}
          {!abertura.interno && <span className="sr-only">(abre em outra aba)</span>}
        </span>
      ) : (
        <ChipDoCard card={{ trancado: card.trancado || travado }} sobreNavy />
      )}
    </>
  );

  // Largura total da grade, não duas de três colunas: com `col-span-2` sobrava
  // um buraco na linha seguinte e o herói parecia desalinhado em vez de grande.
  const classe =
    "painel-navy rise flex min-h-36 flex-wrap items-center justify-between gap-4 rounded-[1.125rem] p-6 sm:col-span-2 lg:col-span-3 sm:p-7";
  const estilo = { "--i": 1 } as React.CSSProperties;

  return (
    <LinkDoCard abertura={abertura} className={classe} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

/** As outras três portas universais: Comunidade e as duas do Contrate um Contador. */
function CardPorta({
  card,
  curso,
  i,
  travado,
}: {
  card: CardVitrine;
  curso: string;
  i: number;
  travado?: boolean;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <div className="flex items-start justify-between gap-2">
        <Tile>
          <IconeDoCard slug={card.card_slug} />
        </Tile>
        {abre ? (
          <span className="text-cinza/50">
            <SinalDeAbertura interno={abertura.abre && abertura.interno} />
          </span>
        ) : (
          <ChipDoCard card={{ trancado: card.trancado || travado }} />
        )}
      </div>
      <h3 className="mt-3 font-display font-semibold text-tinta">{card.card_nome}</h3>
      {card.descricao && <p className="mt-1 text-sm text-cinza">{card.descricao}</p>}
    </>
  );

  const base = "surface rise flex min-h-32 flex-col p-5";
  const estilo = { "--i": i } as React.CSSProperties;

  // Card sem URL não é link: o destino ainda mora no Bubble (Fase 4), e um link
  // quebrado é pior que um "em breve" honesto.
  return (
    <LinkDoCard abertura={abertura} className={base} classeFechado={CARD_EM_BREVE} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

function CardFerramenta({
  card,
  curso,
  i,
  travado,
  novidade = null,
}: {
  card: CardVitrine;
  curso: string;
  i: number;
  travado?: boolean;
  /** A atualização DIVULGADA pela equipe (000304), enquanto vale. */
  novidade?: Novidade | null;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <Tile cor={card.card_tipo === "curso_bonus" ? "laranja" : "azul"}>
        <IconeDoCard slug={card.card_slug} />
      </Tile>
      <div className="min-w-0 flex-1">
        <h3 className="font-display font-semibold text-tinta">{card.card_nome}</h3>
        {card.descricao && <p className="mt-0.5 text-sm leading-snug text-cinza">{card.descricao}</p>}
        {/* O chip usa #8a4a00, e não o laranja-escuro dos ícones: aqui é texto
            pequeno sobre fundo claro, e #c76a00 não alcança 4,5:1. */}
        {card.card_tipo === "curso_bonus" && (
          <span className="mt-2 inline-flex items-center rounded-full bg-laranja/15 px-2.5 py-0.5 text-xs font-semibold text-alerta-tinta">
            Curso bônus
          </span>
        )}
        {/* A novidade diz O QUE mudou, e não só que mudou: o selo sozinho pede
            um clique para saber se interessa. Só com a porta aberta — novidade
            de uma ferramenta que não abre (curso trancado) só confunde, e a
            leitura do banco já a tira; aqui é a mesma regra para o selo. */}
        {novidade && abre && (
          <p className="mt-2 text-xs leading-snug text-aviso-tinta" data-novidade={card.card_slug}>
            <span className="mr-1.5 inline-flex items-center rounded-full bg-aviso-fundo px-2 py-0.5 font-semibold">
              Novidade
            </span>
            {novidade.texto}
          </p>
        )}
      </div>
      {abre ? (
        <span className="self-center text-laranja-texto">
          <SinalDeAbertura interno={abertura.abre && abertura.interno} className="size-5" />
        </span>
      ) : (
        <ChipDoCard card={{ trancado: card.trancado || travado }} />
      )}
    </>
  );

  const base = "surface rise flex min-h-26 items-start gap-4 p-5";
  const estilo = { "--i": i } as React.CSSProperties;

  return (
    <LinkDoCard abertura={abertura} className={base} classeFechado={CARD_EM_BREVE} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

/**
 * Card-imagem (N11/D27): o card É a imagem do Bubble, com os selos de estado e a
 * faixa "ACESSAR" (que sobe no hover) por cima. Usado nas áreas que já têm TODAS as
 * imagens (hoje a Escola); fora delas, o card estruturado. Réplica do `.ec-card` do
 * Bubble: raio 16px, sobe ~6px no hover, e o rodapé dourado "ACESSAR" deslizando.
 */
function CardImagem({
  card,
  curso,
  imagem,
  i,
  travado,
  novidade = null,
}: {
  card: CardVitrine;
  curso: string;
  imagem: string;
  i: number;
  travado?: boolean;
  novidade?: Novidade | null;
}) {
  const abertura = aberturaDoCard(card, curso, travado);
  const abre = abertura.abre;
  const conteudo = (
    <>
      <img src={imagem} alt={card.card_nome} loading="lazy" className="block w-full rounded-2xl" />
      {/* A imagem não muda; o estado (em breve, cadeado do contrato) e a novidade
          vivem SOBRE a arte, no canto. */}
      {!abre && (
        <span className="absolute right-3 top-3">
          <ChipDoCard card={{ trancado: card.trancado || travado }} />
        </span>
      )}
      {novidade && abre && (
        <span
          className="absolute left-3 top-3 inline-flex items-center rounded-full bg-aviso-fundo px-2 py-0.5 text-xs font-semibold text-aviso-tinta"
          data-novidade={card.card_slug}
        >
          Novidade
        </span>
      )}
      {/* O "ACESSAR" do Bubble: faixa dourada que sobe do rodapé no hover/foco. */}
      {abre && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-center gap-2 bg-[linear-gradient(135deg,#f0a63f,#e89a38)] px-3 py-3 text-[13px] font-extrabold uppercase tracking-[0.18em] text-[#0b1e42] shadow-[0_-6px_18px_rgba(11,30,66,0.30)] transition-[translate] duration-300 ease-out group-hover:translate-y-0 group-focus-visible:translate-y-0"
        >
          Acessar
          <SetaDireita className="size-4" />
        </span>
      )}
    </>
  );

  // `group` para a faixa "ACESSAR" ouvir o hover. O levantar do card no hover vem
  // do `porta` que o LinkDoCard acrescenta (transform), então aqui não repito o
  // translate — senão as duas regras brigam pela mesma propriedade.
  const base = "group rise relative block overflow-hidden rounded-2xl";
  const estilo = { "--i": i } as React.CSSProperties;

  return (
    <LinkDoCard abertura={abertura} className={base} classeFechado={CARD_EM_BREVE} style={estilo}>
      {conteudo}
    </LinkDoCard>
  );
}

/**
 * O convite para a PESQUISA aberta sobre este curso (000310, o G1). Só aparece
 * enquanto falta resposta: respondida, a faixa vira ruído no alto de uma página
 * que o aluno abre todo dia. O convite diz o que a tela de Avaliações promete —
 * a equipe vê a nota e o comentário, não o nome —, porque é isso que decide se a
 * pessoa responde com franqueza.
 */
function ConviteDaPesquisa({ pesquisas }: { pesquisas: PesquisaDoAluno[] }) {
  const pendente = pesquisas.find((p) => faltamResponder(p) > 0);
  if (!pendente) return null;
  return (
    <div
      className="surface rise mt-4 flex flex-wrap items-center justify-between gap-3 p-5"
      data-convite-pesquisa={pendente.id}
    >
      <div className="min-w-0">
        <p className="font-semibold text-titulo">Sua opinião sobre este curso</p>
        <p className="text-sm text-cinza">
          A pesquisa «{pendente.titulo}» está aberta. Responder leva um minuto, e a equipe não vê o seu
          nome.
        </p>
      </div>
      <Link to="/avaliacoes" className="pill-cta">
        Responder
      </Link>
    </div>
  );
}
